import React, { forwardRef, useImperativeHandle } from "react"

import "./Popover.css"
import classNames from "classnames";

/**
 * Description of the component.
 */

const baseClass = "wb-popover"

const anchorChoices = [
  "topLeft",
  "topCenter",
  "topRight",
  "rightTop",
  "rightCenter",
  "rightBottom",
  "bottomLeft",
  "bottomCenter",
  "bottomRight",
  "leftTop",
  "leftCenter",
  "leftBottom",
]

type PopoverProps = {
  /**
   * Passthrough classname for customising styling of the popover.
   */
  className?: string;
  /**
   * Open state.
   */
  open: boolean;
  /**
   * On close.
   *
   */
  close: () => void;
  /** 
   * Anchor choice. The chosen anchor will act as the "source" from which the popover displays.
   */
  anchor?: typeof anchorChoices[number],
  /** 
   * Anchor choice. The chosen anchor will act as the "source" from which the popover displays.
   */
  origin?: typeof anchorChoices[number],
  /**
   * Anchor element to attach this popover to. (not implemented yet but is required for the popover to work)
   */
  anchorEl?: Element | null,
  /**
   * Elevates the popover above other elements on the page.
   */
  elevation?: "none" | "low" | "medium" | "high" | "high-on-dark-overlay",
  
  /**
   * Border radius of the popover.
   */
  borderRadius?: "none" | "card" | "button",
  /**
   * The popover's contents.
   */
  children?: React.ReactNode;
};

const getStyles = (anchor: string, bounds: DOMRect | null) => {
  if (!bounds) {
    return {}
  }
  switch (anchor) {
    case "topLeft":
      return {
        top: 0,
        left: 0
      }
    case "topCenter":
      return {
        top: 0,
        left: bounds.width / 2
      }
    case "topRight":
      return {
        top: 0,
        left: bounds.width
      }
    case "rightTop":
      return {
        top: 0,
        left: bounds.width
      }
    case "rightCenter":
      return {
        top: bounds.height / 2,
        left: bounds.width
      }
    case "rightBottom":
      return {
        top: bounds.height,
        left: bounds.width
      }
    case "bottomLeft":
      return {
        top: bounds.height,
        left: 0
      }
    case "bottomCenter":
      return {
        top: bounds.height,
        left: bounds.width / 2
      }
    case "bottomRight":
      return {
        top: bounds.height,
        left: bounds.width
      }
    case "leftTop":
      return {
        top: 0,
        left: 0
      }
    case "leftCenter":
      return {
        top: bounds.height / 2,
        left: 0
      }
    case "leftBottom":
      return {
        top: bounds.height,
        left: 0
      }
  }
}
const Popover = forwardRef<HTMLDivElement, PopoverProps>(({
  className,
  open = false,
  close,
  anchor = "topLeft",
  origin = "bottomLeft",
  elevation = "medium",
  anchorEl,
  borderRadius = "card",
  children
}, ref) => {
  // localize inner ref
  const innerRef = React.useRef<HTMLDivElement>(null);
  
  // expose inner ref
  useImperativeHandle(ref, () => innerRef.current!);

  const styles = React.useMemo(() => {
    if (!!anchorEl) {
      return getStyles(anchor, anchorEl.getBoundingClientRect())
    }

    return {}
  }
  , [anchor, anchorEl, open])

  React.useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event) {
      if (open && innerRef?.current && !innerRef.current.contains(event.target) && !anchorEl?.contains(event.target)){
        close();
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, innerRef]);
  return (
    <div ref={innerRef} className={classNames(baseClass, className, {
      [`${baseClass}-open`]: open,
      [`${baseClass}--origin-${origin}`]: origin,
      [`${baseClass}--elevation-${elevation}`]: elevation,
      [`${baseClass}--border-radius-${borderRadius}`]: borderRadius,
    })} style={{
      ...styles
    }}>
      {children}
    </div>
  )
})

export { Popover }
export type { PopoverProps }
